import { useReducer, useCallback } from 'react';
import { SelectOption } from '../lib/pocketbase';

interface FormState {
  auditoren: SelectOption[];
  direktionen: SelectOption[];
  dienststellen: SelectOption[];
  kontrollpunkte: SelectOption[];
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  auditId?: string;
}

type FormAction = 
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SUBMITTING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_DATA'; payload: Partial<FormState> }
  | { type: 'SET_DIENSTSTELLEN'; payload: SelectOption[] }
  | { type: 'SET_AUDIT_ID'; payload: string };

const initialState: FormState = {
  auditoren: [],
  direktionen: [],
  dienststellen: [],
  kontrollpunkte: [],
  isLoading: true,
  isSubmitting: false,
  error: null
};

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_DATA':
      return { ...state, ...action.payload };
    case 'SET_DIENSTSTELLEN':
      return { ...state, dienststellen: action.payload };
    case 'SET_AUDIT_ID':
      return { ...state, auditId: action.payload };
    default:
      return state;
  }
}

export function useFormReducer() {
  const [state, dispatch] = useReducer(formReducer, initialState);

  const setLoading = useCallback((loading: boolean) => 
    dispatch({ type: 'SET_LOADING', payload: loading }), []);

  const setSubmitting = useCallback((submitting: boolean) =>
    dispatch({ type: 'SET_SUBMITTING', payload: submitting }), []);

  const setError = useCallback((error: string | null) =>
    dispatch({ type: 'SET_ERROR', payload: error }), []);

  const setData = useCallback((data: Partial<FormState>) =>
    dispatch({ type: 'SET_DATA', payload: data }), []);

  const setDienststellen = useCallback((dienststellen: SelectOption[]) =>
    dispatch({ type: 'SET_DIENSTSTELLEN', payload: dienststellen }), []);

  const setAuditId = useCallback((id: string) =>
    dispatch({ type: 'SET_AUDIT_ID', payload: id }), []);

  return {
    state,
    setLoading,
    setSubmitting,
    setError,
    setData,
    setDienststellen,
    setAuditId
  };
}
